import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  onIntersect: () => void;
  rootMargin?: string;
  threshold?: number;
  enabled?: boolean;
}

export default function useInfiniteScroll({
  onIntersect,
  rootMargin = '300px',
  threshold = 0.1,
  enabled = true,
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const lastIntersectTime = useRef(0);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const now = Date.now();
      // Debounce intersections to prevent rapid firing
      if (now - lastIntersectTime.current < 500) return;

      const isIntersecting = entries[0]?.isIntersecting;
      if (isIntersecting && enabled) {
        lastIntersectTime.current = now;
        onIntersect();
      }
    },
    [onIntersect, enabled],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !enabled) return;

    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin,
      threshold,
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersect, rootMargin, threshold, enabled]);

  return sentinelRef;
}
